<script setup lang="ts">
import { computed } from 'vue'
import { GENERATION_STAGES, type GenerationStageKey } from './generation_phases'

const DURATION_COPY = 'This usually takes 1-3 minutes. You can close this and keep working.'

// Measured runs carried 141 and 319 events. The lower one is the yardstick, so a typical run
// fills steadily and a long one reaches full and pulses rather than creeping and looking stalled.
const EXPECTED_EVENTS = 140
// Content chunks arrive every ~40ms, so a proportional fill would be visibly static. Eighths are
// big enough to see, and the first event lands a whole step.
const FILL_STEPS = 8
const FULL_PERCENT = 100
const SKELETON_LINE_WIDTHS = ['100%', '92%', '97%', '64%']

const props = withDefaults(defineProps<{
  stage: GenerationStageKey
  /** Counts stream events, whatever their type; it sets the fill, and no event content is read. */
  tick?: number
  /** What is running now, always our own copy, or '' for nothing. */
  detail?: string
}>(), {
  tick: 0,
  detail: ''
})

const stageLabel = computed(() => (GENERATION_STAGES.find(({ key }) => key === props.stage) ?? GENERATION_STAGES[0]).label)
const fillPercent = computed(() => {
  const steps = Math.max(0, Math.ceil((props.tick / EXPECTED_EVENTS) * FILL_STEPS))
  return Math.min(FULL_PERCENT, steps * (FULL_PERCENT / FILL_STEPS))
})
const isFull = computed(() => fillPercent.value >= FULL_PERCENT)
</script>

<template>
  <div class="py-1" role="status" aria-busy="true" aria-atomic="false" data-testid="generation-progress">
    <!-- aria-atomic="false" overrides role="status"'s implied true, so a change re-announces only
         the line that changed, through its own polite region, not the duration copy as well. -->
    <div class="flex items-center gap-5">
      <!-- The finished tile's frame and badge, filling as events arrive. Hidden: the label carries
           the meaning, and a percentage per event over two minutes is unusable. -->
      <div class="relative h-[119px] w-44 flex-none overflow-hidden border border-gray-400 bg-white" aria-hidden="true">
        <span
          class="absolute inset-x-0 bottom-0 border-t border-blue-400 bg-blue-200 transition-[height] duration-500 ease-out motion-reduce:transition-none"
          :class="{ 'animate-pulse motion-reduce:animate-none motion-reduce:border-t-2': isFull }"
          :style="{ height: `${fillPercent}%` }"
          data-testid="generation-progress-fill"
        />
        <span class="absolute right-2 top-2 z-20 rounded-full bg-gradient-to-r from-ai-bluePinkStart from-50% to-ai-bluePinkStop px-2 py-px text-sm font-bold leading-snug tracking-wide text-white">AI</span>
        <div class="absolute inset-0 z-10 flex flex-col items-start px-3.5 py-3">
          <span class="mb-3 h-1.5 w-[52%] rounded-xxs bg-gray-600" />
          <span v-for="width in SKELETON_LINE_WIDTHS" :key="width" class="mb-2 h-0.5 rounded-xxs bg-gray-400" :style="{ width }" />
        </div>
      </div>

      <div class="flex min-w-0 flex-1 items-start gap-2.5 text-left">
        <!-- Continuous, unlike the fill: runs go 15-19s between events, and a display that only
             moves on events would read as a hang. -->
        <span
          class="mt-1 size-3.5 flex-none animate-spin rounded-full border-2 border-blue-400 border-t-transparent motion-reduce:animate-none motion-reduce:border-t-blue-400 motion-reduce:opacity-40"
          aria-hidden="true"
        />
        <div class="flex min-w-0 flex-col">
          <span class="text-lg font-medium" aria-live="polite" data-testid="generation-stage">{{ stageLabel }}</span>
          <!-- Always rendered, so appearing and clearing are text changes a live region announces
               reliably. Keep the interpolation flush against the tags: whitespace would defeat empty:. -->
          <span class="mt-1.5 text-md text-gray-700 empty:mt-0" aria-live="polite" data-testid="generation-detail">{{ detail }}</span>
        </div>
      </div>
    </div>

    <p class="mt-4 text-md text-gray-700">{{ DURATION_COPY }}</p>
  </div>
</template>
